import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { useGalleryRetouchingQaShortcuts } from '@/features/photoset/hooks/use-gallery-retouching-qa-shortcuts'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import { useImageSelectionApproval } from '@/features/photoset/queries/use-image-selection-approval'
import type {
	Flag,
	Image,
	PhotosetSpace,
	Space
} from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { GalleryRetouchingQaSidebar } from './gallery-retouching-qa-sidebar'

interface GalleryRetouchingQaModalProps {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	currentDetailSpaceIndex: number
	onClose: () => void
	currentPhotosetSpaceId: PhotosetSpace['id']
	currentSpaceName: Space['key_name']
	isQaView: boolean
}

export const GalleryRetouchingQaModal: FC<GalleryRetouchingQaModalProps> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	currentDetailSpaceIndex,
	currentPhotosetSpaceId,
	onClose,
	currentSpaceName,
	isQaView
}) => {
	const { t } = useTranslation()
	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	const flagsQuery = useGetFlags(
		{
			params: {
				types: ['visual_retouching'],
				response_type: 'extended'
			}
		},
		{ enabled: false }
	)

	// mutations
	const { mutate: imageSelectionApprovalToggle } = useImageSelectionApproval({
		photosetId: currentImage.photoset_id,
		photosetSpaceId: currentImage.photoset_space_id as number,
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const flagsKeyName = flagsQuery?.data?.map((flag: Flag) => flag.key_name)
	const isSendBackDisabled = useMemo(
		() =>
			!currentImage.flags.some((flag) =>
				flagsKeyName?.includes(flag.key_name)
			),
		[flagsKeyName, currentImage]
	)

	const onToggleSelection = () => {
		if (isSendBackDisabled) {
			return
		}

		imageSelectionApprovalToggle({
			imageId: currentImage.id,
			needs_to_review: !currentImage.needs_to_review
		})
	}

	useGalleryRetouchingQaShortcuts({
		currentImage,
		currentSpaceIndex: currentDetailSpaceIndex,
		selectableImages,
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onToggleSelection,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					endSlot={
						isQaView && (
							<ButtonLegacy
								onClick={(e) => {
									onToggleSelection()
									e.currentTarget.blur()
								}}
								tabIndex={-1}
								type='button'
								variant='secondary'
							>
								<Checkbox
									checked={currentImage.needs_to_review}
									disabled={isSendBackDisabled}
									readOnly
									tabIndex={-1}
								/>
								{t(
									'photoset:retouching_qa.send_back_for_retouching'
								)}
							</ButtonLegacy>
						)
					}
					kickerTitle={spaceNameToLabel[currentSpaceName]}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				isQaView && (
					<GalleryModalSidebar
						content={
							<GalleryRetouchingQaSidebar
								currentImage={currentImage}
								photosetId={currentImage.photoset_id}
								photosetSpaceId={currentPhotosetSpaceId}
							/>
						}
						title={t('common:feedback')}
					/>
				)
			}
			thumbnails={
				currentImage.retouched_thumbnails ??
				currentImage.ai_retouched_thumbnails
			}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'retouched_thumbnails'}
					thumbnailsFallback='ai_retouched_thumbnails'
				/>
			}
		/>
	)
}
